import { Container, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import { Navigate, useLocation, useParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../../api/adminApi";
import useClientContext from "../../../../../context/useClientContext";
import { pageRoutes } from "../../../../../routes";
import { getReferrerPath } from "../../../../../utilities/navigationHelper";
import DocumentActivityPageHeader from "../../common/document-activity/DocumentActivityPageHeader";
import DocumentActivitySummary from "../../common/document-activity/DocumentActivitySummary";
import TotalTimePerPageSection from "../../common/document-activity/TotalTimePerPageSection";
import UserDocumentActivitySection from "../../common/document-activity/UserDocumentActivitySection";

const getFundraisingFileDownloadInfo = withErrorHandling(adminApi.getFundraisingFileDownloadInfo);

const InvestorFundraisingDocumentActivityPage = () => {
  usePageTitle("Document Activity");

  const { clientCode } = useClientContext();
  const { id, fundraisingId, documentId } = useParams();
  const location = useLocation();

  const isDocumentInfoFetchDisabled = !id || !fundraisingId || !documentId;
  const getDocumentInfo = useCallback(
    () => adminApi.getFundraisingDocumentInfo(defined(fundraisingId), defined(documentId), { investorId: defined(id) }),
    [documentId, fundraisingId, id]
  );

  const [documentInfo, fetchDocumentInfoError, { isFetching: isFetchingDocumentInfo }] = useFetch(getDocumentInfo, {
    disabled: isDocumentInfoFetchDisabled,
  });

  const isAggregatedDocumentViewsFetchDisabled = !id || !documentId;
  const getAggregatedDocumentViews = useCallback(
    () => adminApi.getAggregatedFundraisingDocumentViewsByInvestor(defined(documentId), defined(id)),
    [documentId, id]
  );

  const [
    agggregatedDocumentViews,
    fetchAggregatedDocumentViewsError,
    { isFetching: isFetchingAggregatedDocumentViews },
  ] = useFetch(getAggregatedDocumentViews, { disabled: isAggregatedDocumentViewsFetchDisabled });

  const getAllDocumentViews = useCallback(
    () =>
      adminApi.searchDocumentViews({
        range: { skip: 0, take: 250, totals: false },
        investorIds: [defined(id)],
        fundraisingId: defined(fundraisingId),
        fundraisingDocumentId: defined(documentId),
      }),
    [documentId, fundraisingId, id]
  );

  const [allDocumentViews, fetchAllDocumentViewsError, { isFetching: isFetchingAllDocumentViews }] = useFetch(
    getAllDocumentViews,
    { disabled: isDocumentInfoFetchDisabled }
  );

  const getFileDownloadInfo = useCallback(
    () => getFundraisingFileDownloadInfo(defined(fundraisingId), defined(documentId)),
    [documentId, fundraisingId]
  );

  if (!id || !fundraisingId || !documentId) {
    return <Navigate to={`/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.investors}`} />;
  }

  const fetchError = fetchDocumentInfoError || fetchAggregatedDocumentViewsError || fetchAllDocumentViewsError;
  if (fetchError && fetchError.code !== 404) {
    logError(fetchError, "[InvestorFundraisingDocumentActivityPage]");
    return <DataLoadingFailed />;
  }

  if (isFetchingDocumentInfo || isFetchingAggregatedDocumentViews) {
    return <InlineLoader />;
  }

  const backPath =
    getReferrerPath(location.state) ??
    `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.investors}/${id}/fundraising-documents`;

  const documentViews = (agggregatedDocumentViews ?? []).find((view) => view.fundraisingDocumentId === documentId);

  return (
    <>
      <DocumentActivityPageHeader
        backPath={backPath}
        fileName={documentInfo?.fileName}
        categoryId={documentInfo?.categoryId}
        fundNames={documentInfo?.fundNames ?? []}
        getFileDownloadInfo={getFileDownloadInfo}
      />
      <Container maxWidth="lg" sx={{ height: "100%" }}>
        <Stack spacing={2} sx={{ py: 2.5, width: "100%", height: "100%" }}>
          <Typography variant="h6" sx={{ pt: 2 }}>
            {documentInfo?.investorName || "Investor"} activity
          </Typography>
          {documentViews && <DocumentActivitySummary aggregatedDocumentViews={documentViews} />}
          {documentViews && <TotalTimePerPageSection aggregatedDocumentViews={documentViews} />}
          {allDocumentViews && (
            <UserDocumentActivitySection
              documentViews={allDocumentViews?.items ?? []}
              loading={isFetchingAllDocumentViews}
            />
          )}
        </Stack>
      </Container>
    </>
  );
};

export default InvestorFundraisingDocumentActivityPage;
